import { useState, useEffect } from 'react';
import { Camera } from 'lucide-react';
import { useSystemUsageTracker } from '../hooks/useSystemUsageTracker';
import { useCredits } from '../hooks/useCredits';
import { safeInvoke } from '../lib/safeInvoke';
import { PhotoPrivacyNotice } from '../components/shared/PhotoPrivacyNotice';
import { ReadingFeedback } from '../components/shared/ReadingFeedback';
import { ReadingActions } from '../components/shared/ReadingActions';
import { Spinner } from '../components/ui/Spinner';
import { useNavigate } from 'react-router-dom';
import { takeOrPickPhoto } from '../lib/native/camera';
import { haptics } from '../lib/native/haptics';
import { useI18n } from '../hooks/useI18n';

interface QuiromancyResult {
  handType: string;
  lifeLineSummary: string;
  heartLineSummary: string;
  headLineSummary: string;
  fateLineSummary: string;
  synthesis: string;
  readingId: string | null;
  text: string;
}

function PhaseLoader() {
  const { t }             = useI18n();
  const [phase, setPhase] = useState(0);
  const phases = [
    t('pages.quiromancy.loading1'),
    t('pages.quiromancy.loading2'),
    t('pages.quiromancy.loading3'),
    t('pages.quiromancy.loading4'),
  ];

  useEffect(() => {
    const timer = setInterval(() => setPhase(p => Math.min(p + 1, phases.length - 1)), 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
      <Spinner size="lg" />
      <p className="animate-fade-in" key={phase} style={{ color: 'var(--text-secondary)', marginTop: '1.5rem', fontSize: 'var(--text-label)', fontStyle: 'italic' }}>
        {phases[phase]}
      </p>
    </div>
  );
}

const TABS = [
  { key: 'synthesis',        label: '✨ Síntese' },
  { key: 'lifeLineSummary',  label: '❤️ Linha da Vida' },
  { key: 'heartLineSummary', label: '💜 Linha do Coração' },
  { key: 'headLineSummary',  label: '🧠 Linha da Cabeça' },
  { key: 'fateLineSummary',  label: '🌟 Linha do Destino' },
];

type Hand = 'left' | 'right';

export function QuiromancyPage() {
  useSystemUsageTracker('quiromancia');
  const { credits, refresh } = useCredits();
  const navigate             = useNavigate();
  const { t }                = useI18n();

  const [consent,   setConsent]   = useState(false);
  const [hand,      setHand]      = useState<Hand>('right');
  const [loading,   setLoading]   = useState(false);
  const [result,    setResult]    = useState<QuiromancyResult | null>(null);
  const [error,     setError]     = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('synthesis');

  const noCredits = credits.quiromancy <= 0;

  async function handlePickPhoto() {
    if (!consent) return;
    const photo = await takeOrPickPhoto();
    if (!photo) return;
    await analyze(photo.base64, photo.mimeType);
  }

  async function analyze(imageBase64: string, mimeType: string) {
    setLoading(true);
    setError(null);
    setResult(null);

    const { data, fromFallback } = await safeInvoke<QuiromancyResult>('quiromancy', {
      body:     { imageBase64, mimeType, hand },
      fallback: { handType: '', lifeLineSummary: '', heartLineSummary: '', headLineSummary: '', fateLineSummary: '', synthesis: '', readingId: null, text: '' },
      timeout:  30_000,
    });

    setLoading(false);
    if (fromFallback || !data.synthesis) {
      setError(t('pages.quiromancy.errorMsg'));
      return;
    }
    await haptics.medium();
    setResult(data);
    refresh();
  }

  const tabContent: Record<string, string> = result ? {
    synthesis:        result.synthesis,
    lifeLineSummary:  result.lifeLineSummary,
    heartLineSummary: result.heartLineSummary,
    headLineSummary:  result.headLineSummary,
    fateLineSummary:  result.fateLineSummary,
  } : {};

  return (
    <div className="page-container">
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 className="font-display" style={{ fontSize: 'clamp(1.375rem,4vw,1.875rem)', color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
          {t('pages.quiromancy.title')}
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
          {t('pages.quiromancy.subtitle')}
        </p>
      </div>

      <PhotoPrivacyNotice consentGiven={consent} onConsentChange={setConsent} />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <span className="credit-pill">🖐️ {credits.quiromancy ?? 0} crédito{(credits.quiromancy ?? 0) !== 1 ? 's' : ''}</span>
        {noCredits && <button className="btn btn-primary btn-sm" onClick={() => navigate('/wallet')}>Comprar créditos ✦</button>}
      </div>

      {!result && !loading && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          {/* Hand selector */}
          <div style={{ marginBottom: '1.25rem' }}>
            <p className="form-label" style={{ marginBottom: '0.5rem' }}>{t('pages.quiromancy.handSelectorLabel')}</p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              {(['right', 'left'] as Hand[]).map(h => (
                <button key={h} type="button" onClick={() => setHand(h)}
                  className={hand === h ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
                  style={{ flex: 1 }}>
                  {h === 'right' ? t('pages.quiromancy.rightHand') : t('pages.quiromancy.leftHand')}
                </button>
              ))}
            </div>
            <p className="form-hint">
              {hand === 'right' ? t('pages.quiromancy.rightHandHint') : t('pages.quiromancy.leftHandHint')}
            </p>
          </div>

          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '1.25rem', lineHeight: 1.6 }}>
            {t('pages.quiromancy.instructions')}
          </p>

          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" disabled={!consent || noCredits}
              onClick={handlePickPhoto}
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flex: 1 }}>
              <Camera size={18} /> {t('pages.quiromancy.cameraBtn')}
            </button>
          </div>

          {!consent && (
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginTop: '0.75rem' }}>
              {t('pages.quiromancy.noConsent')}
            </p>
          )}
        </div>
      )}

      {loading && <PhaseLoader />}

      {error && !loading && (
        <div style={{ padding: '1rem', backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 'var(--radius-md)', color: '#fca5a5', fontSize: 'var(--text-label)', marginBottom: '1.5rem' }}>
          {error}
          <br />
          <button className="btn btn-secondary btn-sm" style={{ marginTop: '0.75rem' }} onClick={() => setError(null)}>
            {t('pages.quiromancy.errorRetry')}
          </button>
        </div>
      )}

      {result && !loading && (
        <div className="animate-fade-in">
          {result.handType && (
            <div className="card" style={{ marginBottom: '1.5rem', backgroundColor: 'rgba(31,78,95,0.04)', border: '1px solid var(--border-gold)' }}>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.5rem' }}>
                {t('pages.quiromancy.handTypeLabel')}
              </p>
              <p style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 'var(--text-label)' }}>{result.handType}</p>
            </div>
          )}

          {/* Tabs */}
          <div style={{ display: 'flex', gap: '0.35rem', overflowX: 'auto', marginBottom: '1rem', paddingBottom: '0.25rem' }}>
            {TABS.map(t => (
              <button key={t.key} onClick={() => setActiveTab(t.key)}
                className={activeTab === t.key ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
                style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                {t.label}
              </button>
            ))}
          </div>

          <div className="card animate-fade-in" key={activeTab} style={{ marginBottom: '1.5rem' }}>
            <div className="reading-text">
              {(tabContent[activeTab] || '').split('\n').filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
            <ReadingActions pageName="Quiromancia" reading={result.synthesis} signEmoji="🖐️" />
            <button className="btn btn-secondary btn-sm" onClick={() => { setResult(null); setError(null); }}>
              {t('pages.quiromancy.newAnalysis')}
            </button>
          </div>
          <ReadingFeedback feature="quiromancy" readingId={result.readingId ?? undefined} />
        </div>
      )}
    </div>
  );
}
